import React from 'react';
import { useDispatch } from 'react-redux';
import { IconButton, Icon, Tooltip } from '@material-ui/core';
import ThumbDownAltTwoToneIcon from '@material-ui/icons/ThumbDownAltTwoTone';
import ThumbUpAltTwoToneIcon from '@material-ui/icons/ThumbUpAltTwoTone';
import DoubleArrowTwoToneIcon from '@material-ui/icons/DoubleArrowTwoTone';
import RedoIcon from '@material-ui/icons/Redo';
import DeleteForeverTwoToneIcon from '@material-ui/icons/DeleteForeverTwoTone';
import AudioControlIcon from 'components/AudioControlIcon';
import {
  approveTrackSubmission,
  declineTrackSubmission,
  destroyTrackSubmission,
  forceApproveTrackSubmission,
  requestTrackSubmissionRevision,
} from 'store/trackSubmissions';
import { selectPermission, selectUserUuid } from 'store/selectors';
import { useNlSelector } from 'utils/redux';

const PENDING_FINAL_FEEDBACK = 'Pending Final Feedback';

interface AllProps {
  track: Nl.Api.TrackSubmission | Nl.Api.SingleTrackSubmission;
  disablePlayBtn?: boolean;
}

export const TrackSubmissionsActionsView: React.SFC<AllProps> = ({
  track,
  disablePlayBtn,
}) => {
  const dispatch = useDispatch();

  const userUuid = useNlSelector(selectUserUuid);
  const hasApprovePermission = useNlSelector((state) =>
    selectPermission(state, 'CAN_APPROVE_NEW_TRACK_SUBMISSIONS'),
  );
  const hasForceApprovePermission = useNlSelector((state) =>
    selectPermission(state, 'CAN_FORCE_APPROVE_NEW_TRACK_SUBMISSIONS'),
  );

  const renderEmptyIcon = () => (
    <IconButton disabled>
      <Icon />
    </IconButton>
  );

  const renderApprovalAction = () => {
    const canApprove =
      track.is_approvable &&
      (!track.last_approver || track.last_approver.uuid !== userUuid) &&
      hasApprovePermission;
    if (canApprove) {
      return (
        <Tooltip title='Approve Submission'>
          <IconButton
            data-e2e='approveSubmission'
            onClick={() =>
              dispatch(approveTrackSubmission({ uuid: track.uuid }))
            }
            className='approve'
          >
            <ThumbUpAltTwoToneIcon />
          </IconButton>
        </Tooltip>
      );
    }
    const canForceApprove =
      track.status === PENDING_FINAL_FEEDBACK && hasForceApprovePermission;
    if (canForceApprove) {
      return (
        <Tooltip title='Force-approve submission'>
          <IconButton
            data-e2e='forceApproveSubmission'
            onClick={() =>
              dispatch(forceApproveTrackSubmission({ uuid: track.uuid }))
            }
            className='forceApprove'
          >
            <DoubleArrowTwoToneIcon />
          </IconButton>
        </Tooltip>
      );
    }
    return renderEmptyIcon();
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {track.source_file && !disablePlayBtn ? (
        <AudioControlIcon
          audioTrack={{
            uuid: track.source_file.uuid,
            trackType: 'song',
            url: track.source_file.url,
            trackName: track.name,
            duration: track.source_file.duration ?? 0,
          }}
        />
      ) : (
        renderEmptyIcon()
      )}
      {renderApprovalAction()}
      {
        // This icon requires no empty icon as it only appears in single track view
        track.is_revisable && (
          <Tooltip title='Request a Revision'>
            <IconButton
              data-e2e='requestSubmissionRevision'
              onClick={() =>
                dispatch(requestTrackSubmissionRevision({ uuid: track.uuid }))
              }
              className='revise'
              disabled={!track.is_revisable}
            >
              <RedoIcon />
            </IconButton>
          </Tooltip>
        )
      }
      {track.is_rejectable ? (
        <Tooltip title='Decline Submission'>
          <IconButton
            data-e2e='declineSubmission'
            onClick={() => {
              if (
                window.confirm('Are you sure you want to DECLINE this track?')
              ) {
                dispatch(declineTrackSubmission({ uuid: track.uuid }));
              }
            }}
            className='reject'
            disabled={!track.is_rejectable}
          >
            <ThumbDownAltTwoToneIcon />
          </IconButton>
        </Tooltip>
      ) : (
        renderEmptyIcon()
      )}
      {track.is_deletable ? (
        <Tooltip title='Delete Submission Permanently'>
          <IconButton
            data-e2e='deleteSubmission'
            onClick={() => {
              if (
                window.confirm('Are you sure you want to DELETE this track?')
              ) {
                dispatch(destroyTrackSubmission({ uuid: track.uuid }));
              }
            }}
            className='destroy'
            disabled={!track.is_deletable}
          >
            <DeleteForeverTwoToneIcon color='action' />
          </IconButton>
        </Tooltip>
      ) : (
        renderEmptyIcon()
      )}
    </div>
  );
};

export default TrackSubmissionsActionsView;
